// SPDX-License-Identifier: AGPL-3.0-or-later

import {Logger} from '@app/features/platform/utils/AppLogger';

const logger = new Logger('RoomAmbience');

// −6 dB while someone talks.
export const AMBIENCE_DUCK_GAIN = 10 ** (-6 / 20);
// A quarter while a picture plays on a cinema's screen, faded over about a second (3 time constants) each way.
export const AMBIENCE_TV_GAIN = 0.25;
const TV_FADE_TIME_CONSTANT_S = 0.33;
const RAMP_TIME_CONSTANT_S = 0.15;

export interface RoomSoundSource {
	url: string;
	gain: number;
}

export interface RoomAmbience {
	// 0 to 1; 0 is off.
	setVolume(volume: number): void;
	setDucked(ducked: boolean): void;
	setScreenPlaying(playing: boolean): void;
	setDeafened(deafened: boolean): void;
	dispose(): void;
}

// A room sound file, decoded on the shared voice context. Any failure is null: a room without its sound still works.
export async function loadRoomSound(
	context: AudioContext,
	url: string,
	signal: AbortSignal,
): Promise<AudioBuffer | null> {
	try {
		const response = await fetch(url, {signal});
		if (!response.ok) throw new Error(`Room sound request failed with ${response.status}`);
		const data = await response.arrayBuffer();
		signal.throwIfAborted();
		return await context.decodeAudioData(data);
	} catch (error) {
		if (!signal.aborted) logger.debug('Room sound did not load', {url, error});
		return null;
	}
}

// The room's ambience loop: one decoded buffer, one looping source, one gain into the shared context's destination
// (its soft clip and output device). The file is fetched only once the loop is audible.
export function createRoomAmbience({
	context,
	sound,
	volume: initialVolume,
}: {
	context: AudioContext;
	sound: RoomSoundSource;
	volume: number;
}): RoomAmbience {
	const abort = new AbortController();
	const gain = context.createGain();
	gain.connect(context.destination);
	let source: AudioBufferSourceNode | null = null;
	let loading = false;
	let volume = initialVolume;
	let ducked = false;
	let screenPlaying = false;
	let deafened = false;

	const level = (): number =>
		deafened ? 0 : volume * sound.gain * (ducked ? AMBIENCE_DUCK_GAIN : 1) * (screenPlaying ? AMBIENCE_TV_GAIN : 1);
	const start = (buffer: AudioBuffer | null): void => {
		if (!buffer || abort.signal.aborted) return;
		source = context.createBufferSource();
		source.buffer = buffer;
		source.loop = true;
		source.connect(gain);
		source.start();
	};
	const apply = (timeConstant = RAMP_TIME_CONSTANT_S): void => {
		const next = level();
		gain.gain.setTargetAtTime(next, context.currentTime, timeConstant);
		if (next > 0 && !loading) {
			loading = true;
			void loadRoomSound(context, sound.url, abort.signal).then(start);
		}
	};
	gain.gain.value = level();
	apply();

	return {
		setVolume(next) {
			volume = next;
			apply();
		},
		setDucked(next) {
			ducked = next;
			apply();
		},
		setScreenPlaying(next) {
			screenPlaying = next;
			apply(TV_FADE_TIME_CONSTANT_S);
		},
		setDeafened(next) {
			deafened = next;
			apply();
		},
		dispose() {
			abort.abort();
			if (source) {
				source.stop();
				source.disconnect();
				source.buffer = null;
				source = null;
			}
			gain.disconnect();
		},
	};
}
